/**
 * S-57: 利用者データエクスポート用の汎用 CSV シリアライザ（純関数）。
 *
 * 契約本体第17条2項（契約終了時のデータ救済）で求められる「汎用形式（CSV／JSON）」の
 * うち CSV 側を担う。Firestore doc の配列を RFC 4180 準拠の CSV 文字列に変換する。
 * Web SDK / Firebase 非依存のため `@kawai/shared` に置き、フロント・バックエンド双方から使える。
 */
/**
 * オブジェクト配列を CSV 文字列に変換する。
 *
 * - 1 行目はヘッダ（列名）。
 * - 値が object / array の場合は JSON 文字列化してセルに格納する（ネストの平坦化はしない）。
 * - null / undefined は空セル。
 * - `"` `,` 改行 を含む値はダブルクォートで囲み、内部の `"` は `""` にエスケープする。
 */
export function toCsv(rows, options = {}) {
    return toCsvParts(rows, options).join('');
}
/** S-88: toCsvParts の 1 チャンクに含める最大行数。 */
const CSV_PART_ROWS = 1000;
/**
 * S-88: オブジェクト配列を CSV の文字列チャンク配列に変換する（大規模データ用）。
 *
 * `toCsv` と同一の出力を `parts.join('')` で得られるが、単一の巨大文字列を組み立てず
 * `CSV_PART_ROWS` 行ごとのチャンクで返すため、そのまま `Blob(parts)` に渡すことで
 * 巨大 CSV（行動ログ数万件）でもピークメモリを抑えられる。列（ヘッダ）は全行のキー
 * 和集合から決めるため、後半の行にしか無いフィールドも欠落しない。
 */
export function toCsvParts(rows, options = {}) {
    const newline = options.newline ?? '\r\n';
    const columns = options.columns ?? collectColumns(rows);
    const parts = [columns.map(escapeCell).join(',')];
    for (let i = 0; i < rows.length; i += CSV_PART_ROWS) {
        const chunk = rows.slice(i, i + CSV_PART_ROWS);
        parts.push(chunk
            .map((row) => newline + columns.map((col) => escapeCell(formatValue(row[col]))).join(','))
            .join(''));
    }
    return parts;
}
function collectColumns(rows) {
    const seen = new Set();
    const cols = [];
    for (const row of rows) {
        for (const key of Object.keys(row)) {
            if (!seen.has(key)) {
                seen.add(key);
                cols.push(key);
            }
        }
    }
    return cols;
}
/**
 * S-88 review: CSV/数式インジェクション（CWE-1236）対策。エクスポートは LINE 利用者が
 * 任意に設定できる文字列（表示名・予約の要望等）を含み、Excel で開く前提（BOM 付与）の
 * 成果物のため、セル先頭の数式開始文字を OWASP 推奨のシングルクォート前置で中和する。
 * 文字列値のみ対象（number の String 化 "-5" 等のデータ値は変形しない）。
 */
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;
function neutralizeFormula(value) {
    return FORMULA_TRIGGER.test(value) ? `'${value}` : value;
}
function formatValue(value) {
    if (value === null || value === undefined)
        return '';
    if (typeof value === 'string')
        return neutralizeFormula(value);
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    // object / array は JSON 文字列としてセルに入れる（{ [ " 開始のため数式解釈されない）
    try {
        return JSON.stringify(value);
    }
    catch {
        return String(value);
    }
}
function escapeCell(value) {
    if (/[",\r\n]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}
//# sourceMappingURL=csv.js.map